import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Chip,
  CircularProgress,
  Pagination,
  Divider,
  Alert,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Restore as RestoreIcon,
  InfoOutlined as InfoIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { accrualApi } from '../services/api';
import { format } from 'date-fns';

// Component hiển thị chi tiết thay đổi
const ChangesDetail = ({ changes }) => {
  if (!changes || Object.keys(changes).length === 0) {
    return <Typography variant="body2" color="textSecondary">No detailed changes available</Typography>;
  }

  return (
    <TableContainer component={Paper} variant="outlined" sx={{ mt: 2, maxHeight: 200 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Field</TableCell>
            <TableCell>Old Value</TableCell>
            <TableCell>New Value</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {Object.entries(changes).map(([field, { old, new: newValue }]) => (
            <TableRow key={field}>
              <TableCell>{field}</TableCell>
              <TableCell>{old === null || old === undefined ? '-' : String(old)}</TableCell>
              <TableCell>{newValue === null || newValue === undefined ? '-' : String(newValue)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// Component Tab History
const HistoryTab = ({ campaignId, onRefresh }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [expandedDetail, setExpandedDetail] = useState(null);

  useEffect(() => {
    fetchHistory();
  }, [campaignId, page]);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const response = await accrualApi.getAccrualHistory(campaignId, page);
      setHistory(response.data.results);
      setTotalPages(response.data.total_pages);
      setError(null);
    } catch (err) {
      console.error('Error fetching history:', err);
      setError('Failed to load history. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, value) => {
    setPage(value);
  };

  const toggleDetail = (id) => {
    if (expandedDetail === id) {
      setExpandedDetail(null);
    } else {
      setExpandedDetail(id);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ my: 2 }}>
        {error}
      </Alert>
    );
  }

  if (history.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', p: 4 }}>
        <Typography variant="body1" color="textSecondary">
          No history records found
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>User</TableCell>
              <TableCell>Action</TableCell>
              <TableCell>Media Type</TableCell>
              <TableCell>Month</TableCell>
              <TableCell>Details</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {history.map((record) => (
              <React.Fragment key={record.id}>
                <TableRow>
                  <TableCell>
                    {format(new Date(record.changed_at), 'yyyy-MM-dd HH:mm')}
                  </TableCell>
                  <TableCell>{record.changed_by}</TableCell>
                  <TableCell>
                    <Chip
                      label={record.change_type.toUpperCase()}
                      color={
                        record.change_type === 'create'
                          ? 'success'
                          : record.change_type === 'update'
                          ? 'primary'
                          : 'error'
                      }
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{record.media_type}</TableCell>
                  <TableCell>{record.month ? record.month.substring(0, 7) : 'N/A'}</TableCell>
                  <TableCell>
                    <Tooltip title="View changes">
                      <IconButton
                        size="small"
                        onClick={() => toggleDetail(record.id)}
                      >
                        <InfoIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
                {expandedDetail === record.id && (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ py: 0 }}>
                      <Box sx={{ p: 2, bgcolor: '#f9f9f9' }}>
                        <ChangesDetail changes={record.changes} />
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        <Pagination
          count={totalPages}
          page={page}
          onChange={handleChangePage}
          color="primary"
        />
      </Box>
    </>
  );
};

// Component Tab Versions
const VersionsTab = ({ campaignId, onRefresh }) => {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchVersions();
  }, [campaignId, page]);

  const fetchVersions = async () => {
    setLoading(true);
    try {
      const response = await accrualApi.getDataVersions(campaignId, page);
      setVersions(response.data.results);
      setTotalPages(response.data.total_pages);
      setError(null);
    } catch (err) {
      console.error('Error fetching versions:', err);
      setError('Failed to load versions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, value) => {
    setPage(value);
  };

  const handleRestore = async (versionId) => {
    if (!window.confirm('Are you sure you want to restore this version? All current data will be replaced.')) {
      return;
    }

    setRestoring(true);
    try {
      await accrualApi.restoreVersion(campaignId, versionId);
      alert('Version restored successfully');
      onRefresh(); // Refresh parent component
    } catch (err) {
      console.error('Error restoring version:', err);
      alert('Failed to restore version. Please try again.');
    } finally {
      setRestoring(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ my: 2 }}>
        {error}
      </Alert>
    );
  }

  if (versions.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', p: 4 }}>
        <Typography variant="body1" color="textSecondary">
          No versions found
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Label</TableCell>
              <TableCell>Created By</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {versions.map((version) => (
              <TableRow key={version.id}>
                <TableCell>
                  {format(new Date(version.created_at), 'yyyy-MM-dd HH:mm')}
                </TableCell>
                <TableCell>{version.label}</TableCell>
                <TableCell>{version.created_by}</TableCell>
                <TableCell>
                  {version.has_snapshot && (
                    <Button
                      startIcon={<RestoreIcon />}
                      size="small"
                      variant="outlined"
                      onClick={() => handleRestore(version.id)}
                      disabled={restoring}
                    >
                      Restore
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        <Pagination
          count={totalPages}
          page={page}
          onChange={handleChangePage}
          color="primary"
        />
      </Box>
    </>
  );
};

// Component chính
const HistoryDialog = ({ open, onClose, campaignId, campaignName }) => {
  const [tabIndex, setTabIndex] = useState(0);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  const handleRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            History & Versions {campaignName && `- ${campaignName}`}
          </Typography>
          <IconButton edge="end" color="inherit" onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <Divider />
      
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabIndex} onChange={handleTabChange} aria-label="history tabs">
          <Tab label="Change History" />
          <Tab label="Versions" />
        </Tabs>
      </Box>
      
      <DialogContent>
        {tabIndex === 0 ? (
          <HistoryTab 
            campaignId={campaignId} 
            key={`history-${refreshTrigger}`}
            onRefresh={handleRefresh}
          />
        ) : (
          <VersionsTab 
            campaignId={campaignId} 
            key={`versions-${refreshTrigger}`}
            onRefresh={handleRefresh}
          />
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HistoryDialog; 